import { Suspense, lazy } from "react";
import { Route, Routes } from "react-router";
import { ProtectedRoute } from "./ProtectedRoute";
import { PageLoader } from "@/components/ui/PageLoader";
import { Faqs } from "@/screens/marketing/Faqs";
import { Home } from "@/screens/marketing/Home";
import { NotFound } from "@/screens/marketing/NotFound";
import { Resources } from "@/screens/marketing/Resources";
import { AuthAction } from "@/screens/user/AuthAction";
import { SignIn } from "@/screens/user/SignIn";

/*
  Every screen behind sign-in is split out of the entry chunk.

  These are the only screens that touch Firestore, and `/` is the most-visited
  page in the app — it was shipping the whole database SDK to render prose.
  Nobody signed out ever executes a line of this, so nobody signed out should
  have to download it.

  The public screens stay eager on purpose: a Suspense fallback on the landing
  page would trade bytes for a flash of loader, which is the wrong way round for
  the page an advert lands on.
*/
const Dashboard = lazy(() => import("@/screens/Dashboard").then((m) => ({ default: m.Dashboard })));
const Performance = lazy(() =>
  import("@/screens/Performance").then((m) => ({ default: m.Performance })),
);
const Settings = lazy(() => import("@/screens/user/Settings").then((m) => ({ default: m.Settings })));
const TradeList = lazy(() =>
  import("@/screens/trade/TradeList").then((m) => ({ default: m.TradeList })),
);
const TradeDetail = lazy(() =>
  import("@/screens/trade/TradeDetail").then((m) => ({ default: m.TradeDetail })),
);
const TradeForm = lazy(() =>
  import("@/screens/trade/TradeForm").then((m) => ({ default: m.TradeForm })),
);

/**
 * Route table ported from legacy/App.js.
 *
 * Kept separate from <App> so tests can mount it inside a MemoryRouter. Every
 * route now points at a real screen; the `NotPorted` placeholder that stood in
 * through Phases 4 and 5 is gone.
 */
export function AppRoutes() {
  return (
    // One boundary around the table rather than one per route: the fallback is
    // the same loader either way, and the guard already shows it while auth
    // resolves — so a signed-in user sees one loader, not two in sequence.
    <Suspense fallback={<PageLoader />}>
      <Routes>
      {/* Public */}
      <Route path="/" element={<Home />} />
      <Route path="/user/sign-in" element={<SignIn />} />
      <Route path="/user/start" element={<SignIn mode="signUp" />} />
      {/* Where Firebase's emailed links land, once the console's action URL
          points here instead of at the default firebaseapp.com page. Public by
          necessity: the user is usually signed out when they click it. */}
      <Route path="/auth/action" element={<AuthAction />} />
      {/* `/donate` is retired, not moved — it falls through to the 404 with
          everything else. See Phase 5 in MIGRATION.md. */}
      <Route path="/faqs" element={<Faqs />} />
      <Route path="/resources" element={<Resources />} />

      {/* Authenticated */}
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <Dashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/trade/list"
        element={
          <ProtectedRoute>
            <TradeList />
          </ProtectedRoute>
        }
      />
      {/* Split from the old `/trade/form/:id?` — two explicit routes read more
          clearly than an optional segment. */}
      <Route
        path="/trade/form"
        element={
          <ProtectedRoute>
            <TradeForm />
          </ProtectedRoute>
        }
      />
      <Route
        path="/trade/form/:id"
        element={
          <ProtectedRoute>
            <TradeForm />
          </ProtectedRoute>
        }
      />
      <Route
        path="/trade/:id"
        element={
          <ProtectedRoute>
            <TradeDetail />
          </ProtectedRoute>
        }
      />
      <Route
        path="/performance"
        element={
          <ProtectedRoute>
            <Performance />
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings"
        element={
          <ProtectedRoute>
            <Settings />
          </ProtectedRoute>
        }
      />

      <Route path="*" element={<NotFound />} />
      </Routes>
    </Suspense>
  );
}
